import { ConfigRecord } from "./ConfigRecord";
import { IconDefinition, ChemicalType, IconDefinitionObj } from "../../types";
import { default as Locale } from '../../utils/Locale';
import { ChlorineSensorSensorTypes, ChloirneSensorGains } from "../../catalog";

export class FlowItem {
    UUID: string;
    ConnectedThings?: FlowItem[]
};
export class CombinedControlItem {
    ControlUUID: string;
}
export class CombinedControlConfig {
    UUID: string;
    ControlGroupUUID: string;
    Controls: CombinedControlItem[];
    Name: string;
};
export class ControlConfig {
    Name: string;
    UUID: string;
    BodyOfWater: string;
    Type: ControlTypes;
    ControlGroupUUID: string;
    Priority: number;
    Light?: string;
};
export enum ControlTypes {
    Blower = "Blower",
    Cleaner = "Cleaner",
    Fire = "Fire",
    Filter = "Filter",
    GenericOnOff = "GenericOnOff",
    Heating = "Heating",
    Light = "Light",
    /** @deprecated */
    Thermostat = "Thermostat",
    WaterFeature = "WaterFeature",
}

export class DeviceConfig {
    UUID: string;
    /** Not the same as hardwareDeviceConfig.DeviceType.
     * This prop can be e.g: SaltCell, Pump etc.*/
    DeviceType: DeviceTypes;
    EnablePowerPort?: boolean;
    Name: string;
    ChemicalType?: ChemicalType;
    Icon?: IconDefinition;
    HardwareDevice?: HardwareDeviceConfig = new HardwareDeviceConfig();
};
export class PortConfig {
    Type: PortTypeValues;
    Number: number;
};

/**
 * Possible values of HardwareDevice.Port.Type property in HardwareConfig.
 * @see PlatformCapabilities
 */
export enum PortTypeValues {
    Actuator = "Actuator",
    Bluetooth = "Bluetooth",
    Chemistry420 = "Chemistry420",
    ChemistryORP = "ChemistryORP",
    ChemistryPH = "ChemistryPH",
    /**
     * @deprecated It has never used. Added only for be able to map from PlaltformCapabalility to HWConfig names.
     * @see translatePortTypePlatformCapabilitiesToHwConfigTo()*/
    InterfaceButtons = "InterfaceButtons",
    HighVoltage = "HighVoltage",
    LightDriverLowVoltage = "LightDriverLowVoltage",
    LowVoltage = "LowVoltage",
    PoolsideButtonInterface = "PoolsideButtonInterface",
    Serial = "Serial",
    Switch = "Switch",
    Thermistor = "Thermistor",
};

export class HardwareDeviceConfig {
    /** Introduced in AutoFilterVersion v12 */
    ActuatorSettings?: ActuatorSettings;
    AddressMap?: any;
    AutoFillSettings?: { [ key: string ]: any };
    /** @deprecated Moved into ActuatorSetting in AutoFilterVersion vXX */
    InterlockedActuator?: string;
    PowerPort?: PortConfig;
    Serial?: string;
    Make?: string;
    Model?: string;
    Module?: string;
    Controller?: string;
    Ports?: PortConfig[] = [];
    UniqueBusIdentifier?: string;
    /** Not the same as DeviceConfig.DeviceType!
     * This field can be e.g: IntelliChlor, AquaRite, AquaRite */
    DeviceType?: string;
    PrimaryCatalogId?: string;
    SecondaryCatalogId?: string;
    WaterLevelSensorSettings?:WaterLevelSensorSettings;
    CoverSettings?:CoverSettings;
    PumpSettings?:PumpSettings;
    ChemicalPumpSettings?:ChemicalPumpSettings;
    ProbeSettings?:ProbeSettings;
    LightSettings?:LightSettings;
    /** @deprecated Moved into ActuatorSetting in AutoFilterVersion v12 */
    DefaultActuatorPosition?:DefaultActuatorPosition;
    /** @deprecated See HVACSettings */
    MinAirOperatingTempF?: number;
    /** @deprecated See HVACSettings */
    MaxAirOperatingTempF?: number;
    /** @deprecated See HVACSettings */
    SupportHeating?: boolean;
    /** @deprecated See HVACSettings */
    SupportCooling?: boolean;
    /** @deprecated See HVACSettings */
    CooldownSeconds?: number;
    HVACSettings?: {
        MinAirOperatingTempF?: number;
        MaxAirOperatingTempF?: number;
        SupportHeating?: boolean;
        SupportCooling?: boolean;
        CooldownSeconds?: number;
    }
};

export type ActuatorSettings = {
    DefaultPosition: DefaultActuatorPosition,
    InterlockedActuator: string,
    /** Introduced in AutoFilterVersion v16 */
    MinimumFlowRatePercentLeft: number,
    /** Introduced in AutoFilterVersion v16 */
    MinimumFlowRatePercentRight: number,
    /** Introduced in AutoFilterVersion v25 */
    WaterInletDirection: string,
}

export enum WaterLevelSensorSettingsTypes {
    LOW = "LOW",
    HIGH = "HIGH",
}

export enum WaterLevelSensorSettingsInterlockActivationStates {
    DISABLED = "DISABLED",
    IN_WATER = "IN_WATER",
    OUT_OF_WATER = "OUT_OF_WATER",
}

export class WaterLevelSensorSettingsInterlockedPumps {
    UUID: string;
}

export class WaterLevelSensorSettings {
    BodyOfWaterUUID: string;
    InterlockActivationState: WaterLevelSensorSettingsInterlockActivationStates;
    InterlockedPumps: WaterLevelSensorSettingsInterlockedPumps[];
    PoweringWaterLevelSensor? : PortConfig;
    ThermistorPortPercentOfVCC? : number;
    Type: WaterLevelSensorSettingsTypes;
    WaterLevelSensor: PortConfig;
}

export class CoverSettings {
    BodyOfWaterUUID: string;
}

export class PumpSettings {
    PrimeDurationMinutes: number;
    PrimeSpeedPercent: number;
    MinimumSpeed: number;
    MaximumSpeed : number;
    MinimumGPM : number;
    MaximumGPM : number;
    ControlMode : string;
}

export class ChemicalPumpSettings {
    GallonsPerDay: number;
    DilutionRatio: number;
    PrimeDurationMinutes: number;
    ChemicalType: ChemicalType;
}

export class ProbeSettings {
    Tweak?: number;
    GainMode?: ChloirneSensorGains;
}

export class LightSettings {
    CompatibleBrandId: string;
    ColorAndShowLibraryId: string;
    ColorAndShowSelectionMethodId: string;
};

export enum DefaultActuatorPosition {
    /** @deprecated Removed by AutoFilterVersion 10. Became LEFT */
    RED = "RED",
    /** @deprecated Removed by AutoFilterVersion 10. Became RIGHT */
    WHITE = "WHITE",
    /** Introduced by AutoFilterVersion 10. */
    LEFT = "LEFT",
    /** Introduced by AutoFilterVersion 10. */
    RIGHT = "RIGHT",
    /** Introduced by AutoFilterVersion 12. */
    NONE = "NONE",
};

export class LocationConfig {
    UUID: string;
    Name: string;
    Address: string;
    City: string;
    State: string;
    Latitude?: number|string = 0;
    Longitude?: number|string = 0;
};

export class BodyOfWaterConfig {
    UUID: string;
    Name: string;
    SizeGallons: number;
    Spillover?: string;
    Location: string;
    Icon?: IconDefinition;
    TemperatureRange?: number[];
    TemperatureControlName = Locale.gettext("Temp");
    PHTarget?: number;
    ORPTarget?: number;
    Winterized?: boolean;
    SanitizerTarget?: {
        Target: number,
        Type: ChlorineSensorSensorTypes
    }
    BodyOfWaterType: "POOL" | "SPA";
};

export class ControlGroupConfig {
    Name: string;
    UUID: string;
    /** 
     * @deprecated Use BodyOfWater prop of the Control instead.
     * In the future we might alllow Control buttons of the App Home screen to be moved betwen groups.
     * E.g. make a ControlGroup named "Heaters" and move all Heat controls there so BOW uuid of the Group won't be relevane anymmore.
     */
    BodyOfWater: string;
};
export class WaterFeatureConfig {
    UUID: string;
    Name: string;
    Icon: IconDefinition;
    SpeedRange: number[];
};
export class ControllerConfig {
    Name: string;
    UUID: string;
    Number: number;
};
export class FlowConfig {
    UUID: string;
    Name: string;
    Pump: string;
    Suction: FlowItem;
    Return: FlowItem;
};
export class FreezeProtectConfig {
    Mode?: FreezeProtectModes;
    MinSpeed?: number;
    ActivatingTemperature?: number;
    AutoPauseMinutes?: number;
    TurnOnHeatingDevicesWhenBelowMinimumTemperature?: boolean;
    AvoidFreezeProtectIfNoInternetWeather?: boolean;
    FreezeProtectFlowCycleIntervalMinutes?: number;
    KeepControlStatesPriorToStartingFreezeProtectUnlessTurnedOffByScheduleDuringFreezeProtect?: boolean;
}
export enum FreezeProtectModes {
    ENABLED = "ENABLED",
    THERMISTOR_ONLY = "THERMISTOR_ONLY",
    INTERNET_ONLY = "INTERNET_ONLY",
    DISABLED = "DISABLED",
}

export const getFreezeProtectModeName = (mode:string) => {
    switch(mode) {
    case FreezeProtectModes.ENABLED: return Locale.gettext('Enabled');
    case FreezeProtectModes.THERMISTOR_ONLY: return Locale.gettext('Thermistor only');
    case FreezeProtectModes.INTERNET_ONLY: return Locale.gettext('Internet only');
    case FreezeProtectModes.DISABLED: return Locale.gettext('Disabled');
    }
    return mode;
}

export type HardwareConfigRecord = ConfigRecord<HardwareConfig>;
export type HardwareConfig = {
    Location: LocationConfig;
    Ambient: {
        Temperature?: string;
    };
    FreezeProtect: FreezeProtectConfig;
    BodiesOfWater: BodyOfWaterConfig[];
    Devices: DeviceConfig[];
    Controls: ControlConfig[];
    CombinedControls: CombinedControlConfig[];
    ControlGroups: ControlGroupConfig[];
    Flows: FlowConfig[];
    WaterFeatures: WaterFeatureConfig[];
    Controllers: ControllerConfig[];
    Settings?: {
        TurnOffPumpsWhileActuatorsAreMoving?: boolean,
        IgnoreActuatorErrorsAndTurnOnPumps?: boolean,
        IgnoreConflictingControls?: boolean,
        AlwaysTurnOnPumpsWhileActuatorsAreMoving?: boolean,
        AllowUsingSerialAddressingForJandyPumps?: boolean
        ShowExpertDeviceConfigurationSettings?: boolean,
    }
}

export enum DeviceTypes {
    ACTUATORTHREEWAY = "ActuatorThreeWay",
    ACTUATORTWOWAY = "ActuatorTwoWay",
    AUTOFILL = "AutoFill",
    BLOWER = "Blower",
    BUTTON= "Button",
    CHEMICALPUMP = "ChemicalPump",
    CHILLER = "Chiller",
    CHLORINESENSOR = "ChlorineSensor",
    CLEANER = "Cleaner",
    COVER = "Cover",
    DOORSENSOR = "DoorSensor",
    DISSOLVEDOXYGENSENSOR = "DissolvedOxygenSensor",
    FILLSWITCH = "FillSwitch",
    FILTER = "Filter",
    FIRE = "Fire",
    FLOWMETER = "FlowMeter",
    FLOWRELAY = "FlowRelay",
    FLOWSWITCH = "FlowSwitch",
    GENERICONOFF = "GenericOnOff",
    HEATER = "Heater",
    HEATPUMP = "HeatPump",
    LIGHT = "Light",
    LIGHTDRIVER = "LightDriver",
    /** @deprecated use ORPPROBE instead */
    ORPROBE = "ORPProbe",
    ORPPROBE = "ORPProbe",
    OZONATOR = "Ozonator",
    PHPROBE = "PHProbe",
    PRESSURESENSOR = "PressureSensor",
    PUMP = "Pump",
    REMOTE = "Remote",
    SALTCELL = "SaltCell",
    SOLARHEATER = "SolarHeater",
    T = "T",
    THERMISTOR = "Thermistor",
    UVSANITIZER = "UvSanitizer",
    WATERLEVELSENSOR = "WaterLevelSensor",
}